import {
  BoxShadowRow,
  Control,
  FilterRow,
  TextShadowRow,
  TransformRow,
  TransitionRow
} from "@/oxygen/classes/lib/types";
import { findLastParentClassThatHasDisplay } from "@/oxygen/classes/lib/classes";
import { DEFAULT_UNIT_TYPES, UnitType } from "@/types/unit";

const customIndex = DEFAULT_UNIT_TYPES.indexOf("custom");
const DEFAULT_MAX_WH_TYPES: UnitType[] = [
  ...DEFAULT_UNIT_TYPES.slice(0, customIndex),
  "none",
  ...DEFAULT_UNIT_TYPES.slice(customIndex)
];

export const classControls: Control[] = [
  {
    slug: "layout",
    name: "Layout",
    type: "accordion",
    open: true,
    controls: [
      {
        slug: "display",
        name: "Display",
        type: "button-bar",
        items: [
          {
            value: "block",
            label: "Block"
          },
          {
            value: "flex",
            label: "Flex"
          },
          {
            value: "grid",
            label: "Grid"
          },
          {
            value: "none",
            label: "None"
          },
          {
            value: "inline-block",
            label: "Inline Block",
            shortLabel: "In. Block"
          },
          {
            value: "inline-flex",
            label: "Inline Flex",
            shortLabel: "In. Flex"
          },
          {
            value: "inline-grid",
            label: "Inline Grid",
            shortLabel: "In. Grid"
          },
          {
            value: "inline",
            label: "Inline"
          },
          {
            value: "contents",
            label: "Contents"
          }
        ],
        hideAfter: 4
      },
      {
        slug: "flex_direction",
        name: "Flow",
        type: "button-bar",
        hideAfter: 3,
        items: [
          {
            value: "row",
            label: "Row",
            icon: "ArrowRightIcon",
            hideLabel: true
          },
          {
            value: "column",
            label: "Column",
            icon: "ArrowDownIcon",
            hideLabel: true
          },
          {
            value: "wrap",
            label: "Wrap",
            icon: "ArrowTurnDownLeftIcon",
            hideLabel: true
          },
          {
            value: "row-reverse nowrap",
            label: "Row Reverse",
            shortLabel: "Row Rev."
          },
          {
            value: "row-reverse wrap",
            label: "Row Reverse Wrap",
            shortLabel: "Row Rev. Wrap"
          },
          {
            value: "column wrap",
            label: "Column Wrap",
            shortLabel: "Col. Wrap"
          },
          {
            value: "column-reverse nowrap",
            label: "Column Reverse",
            shortLabel: "Col. Rev."
          },
          {
            value: "column-reverse wrap",
            label: "Column Reverse Wrap",
            shortLabel: "Col. Rev. Wrap"
          },
          {
            value: "wrap-reverse",
            label: "Wrap-Reverse",
            shortLabel: "Wrap-Rev."
          }
        ],
        conditions: ["layout.display", "in", ["flex", "inline-flex"]]
      },
      {
        slug: "flex_align",
        name: "Align",
        type: "align",
        conditions: ["layout.display", "in", ["flex", "inline-flex"]]
      },
      {
        slug: "grid",
        name: "Grid",
        type: "group",
        columns: 11,
        controls: [
          {
            slug: "simple_grid_template_columns",
            name: "Columns",
            type: "number",
            width: 5,
            positionLabel: "bottom",
            disabledConditions: ["layout.grid.enable_advanced_mode", "==", true]
          },
          {
            slug: "simple_grid_template_rows",
            name: "Rows",
            type: "number",
            width: 5,
            positionLabel: "bottom",
            disabledConditions: ["layout.grid.enable_advanced_mode", "==", true]
          },
          {
            slug: "enable_advanced_mode",
            name: "Enable Advanced Mode",
            type: "button",
            icon: "GearIcon",
            width: 1,
            hideLabel: true
          }
        ],
        conditions: ["layout.display", "in", ["grid", "inline-grid"]]
      },
      {
        slug: "grid_template_columns",
        name: "Columns",
        type: "repeater",
        noDataMessage: "No columns",
        noExpand: true,
        noDisable: true,
        defaultTitle: "Column",
        itemIcon: "LeftAndRightArrowsIcon",
        titleTemplate(row) {
          return `${row.size?.style || "(empty)"}`;
        },
        defaultNewValue: {
          size: {
            number: "1",
            unit: "fr",
            style: "1fr"
          }
        },
        controls: [
          {
            slug: "size",
            name: "Size",
            type: "unit",
            defaultType: "fr",
            types: ["fr", ...DEFAULT_UNIT_TYPES],
            min: 0.25,
            max: 5,
            step: 0.25
          }
        ],
        conditions: [
          ["layout.display", "in", ["grid", "inline-grid"]],
          ["layout.grid.enable_advanced_mode", "==", true]
        ]
      },
      {
        slug: "grid_template_rows",
        name: "Rows",
        type: "repeater",
        noDataMessage: "No rows",
        noExpand: true,
        noDisable: true,
        defaultTitle: "Row",
        itemIcon: "LeftAndRightArrowsIcon",
        titleTemplate(row) {
          return `${row.size?.style || "(empty)"}`;
        },
        defaultNewValue: {
          size: {
            number: "1",
            unit: "fr",
            style: "1fr"
          }
        },
        controls: [
          {
            slug: "size",
            name: "Size",
            type: "unit",
            defaultType: "fr",
            types: ["fr", ...DEFAULT_UNIT_TYPES],
            min: 0.25,
            max: 5,
            step: 0.25
          }
        ],
        conditions: [
          ["layout.display", "in", ["grid", "inline-grid"]],
          ["layout.grid.enable_advanced_mode", "==", true]
        ]
      },
      {
        slug: "grid_area",
        name: "Areas",
        type: "repeater",
        noDisable: true,
        noExpand: true,
        defaultNewValue: {
          name: "Area-{index}"
        },
        defaultTitle: "Area",
        titleTemplate(row) {
          return `${row.name}`;
        },
        noDataMessage: "No areas",
        controls: [
          {
            slug: "name",
            name: "Name",
            type: "text"
          }
        ],
        conditions: [
          ["layout.display", "in", ["grid", "inline-grid"]],
          ["layout.grid.enable_advanced_mode", "==", true]
        ]
      },
      {
        slug: "grid_auto_columns",
        name: "Auto Columns",
        type: "unit",
        defaultType: "fr",
        types: ["fr", ...DEFAULT_UNIT_TYPES],
        conditions: [
          ["layout.display", "in", ["grid", "inline-grid"]],
          ["layout.grid.enable_advanced_mode", "==", true]
        ],
        width: 6,
        positionLabel: "bottom"
      },
      {
        slug: "grid_auto_rows",
        name: "Auto Rows",
        type: "unit",
        defaultType: "fr",
        types: ["fr", ...DEFAULT_UNIT_TYPES],
        conditions: [
          ["layout.display", "in", ["grid", "inline-grid"]],
          ["layout.grid.enable_advanced_mode", "==", true]
        ],
        width: 6,
        positionLabel: "bottom"
      },
      {
        slug: "grid_auto_flow",
        name: "Flow",
        type: "button-bar",
        items: [
          {
            value: "row",
            label: "Row",
            icon: "ArrowRightIcon",
            hideLabel: true
          },
          {
            value: "column",
            label: "Column",
            icon: "ArrowDownIcon",
            hideLabel: true
          }
        ],
        conditions: ["layout.display", "in", ["grid", "inline-grid"]]
      },
      {
        slug: "grid_align",
        name: "Align",
        type: "align",
        conditions: ["layout.display", "in", ["grid", "inline-grid"]]
      },
      {
        slug: "gap",
        name: "Gap",
        type: "gap",
        conditions: [
          "layout.display",
          "in",
          ["grid", "inline-grid", "flex", "inline-flex"]
        ]
      },
      {
        slug: "grid_justify_content",
        name: "Justify",
        type: "button-bar",
        items: [
          {
            value: "start",
            label: "Start",
            icon: "FlexAlignLeftIcon",
            hideLabel: true
          },
          {
            value: "center",
            label: "Center",
            icon: "FlexAlignCenterHorizontalIcon",
            hideLabel: true
          },
          {
            value: "end",
            label: "End",
            icon: "FlexAlignRightIcon",
            hideLabel: true
          },
          {
            value: "stretch",
            label: "Stretch",
            icon: "SquareQuestionIcon",
            hideLabel: true
          },
          {
            value: "space-between",
            label: "Space Between",
            icon: "SquareQuestionIcon",
            hideLabel: true
          },
          {
            value: "space-around",
            label: "Space Around",
            icon: "SquareQuestionIcon",
            hideLabel: true
          }
        ],
        conditions: ["layout.display", "in", ["grid", "inline-grid"]]
      },
      {
        slug: "grid_align_content",
        name: "Align C.",
        type: "button-bar",
        items: [
          {
            value: "start",
            label: "Start",
            icon: "FlexAlignTopIcon",
            hideLabel: true
          },
          {
            value: "center",
            label: "Center",
            icon: "FlexAlignCenterVerticalIcon",
            hideLabel: true
          },
          {
            value: "end",
            label: "End",
            icon: "FlexAlignBottomIcon",
            hideLabel: true
          },
          {
            value: "stretch",
            label: "Stretch",
            icon: "SquareQuestionIcon",
            hideLabel: true
          },
          {
            value: "space-between",
            label: "Space Between",
            icon: "SquareQuestionIcon",
            hideLabel: true
          },
          {
            value: "space-around",
            label: "Space Around",
            icon: "SquareQuestionIcon",
            hideLabel: true
          }
        ],
        conditions: ["layout.display", "in", ["grid", "inline-grid"]]
      }
    ]
  },
  {
    slug: "spacing",
    name: "Spacing",
    type: "accordion",
    controls: [
      {
        slug: "spacing",
        name: "spacing",
        type: "spacing",
        hideLabel: true
      }
    ]
  },
  {
    slug: "size",
    name: "Size",
    type: "accordion",
    controls: [
      {
        slug: "width",
        name: "Width",
        type: "unit",
        width: 6
      },
      {
        slug: "height",
        name: "Height",
        type: "unit",
        width: 6
      },
      {
        slug: "max_width",
        name: "Max W",
        type: "unit",
        width: 6,
        types: DEFAULT_MAX_WH_TYPES
      },
      {
        slug: "max_height",
        name: "Max H",
        type: "unit",
        width: 6,
        types: DEFAULT_MAX_WH_TYPES
      },
      {
        slug: "min_width",
        name: "Min W",
        type: "unit",
        width: 6
      },
      {
        slug: "min_height",
        name: "Min H",
        type: "unit",
        width: 6
      },
      {
        slug: "overflow",
        name: "Overflow",
        type: "button-bar",
        items: [
          {
            label: "Visible",
            value: "visible",
            icon: "EyeSolidIcon",
            hideLabel: true
          },
          {
            label: "Hidden",
            value: "hidden",
            icon: "EyeSlashSolidIcon",
            hideLabel: true
          },
          {
            label: "Scroll",
            value: "scroll",
            icon: "ComputerMouseScrollWheelIcon",
            hideLabel: true
          },
          {
            label: "Clip",
            value: "clip",
            icon: "ScissorsIcon",
            hideLabel: true
          },
          {
            label: "Auto",
            value: "auto"
          }
        ]
      },
      {
        name: "Ratio",
        slug: "aspect_ratio",
        type: "select",
        items: [
          { label: "16:9", value: "16 / 9" },
          { label: "4:3", value: "4 / 3" },
          { label: "3:2", value: "3 / 2" },
          { label: "Square (1:1)", value: "1" },
          { label: "Custom", value: "custom" }
        ]
      },
      {
        slug: "aspect_ratio_custom",
        name: "",
        type: "group",
        controls: [
          {
            slug: "width",
            name: "Width",
            type: "number",
            width: 6,
            positionLabel: "bottom"
          },
          {
            slug: "height",
            name: "Height",
            type: "number",
            width: 6,
            positionLabel: "bottom"
          }
        ],
        conditions: ["size.aspect_ratio", "==", "custom"]
      },
      {
        name: "Obj Fit",
        slug: "object_fit",
        type: "select",
        items: [
          { label: "Fill", value: "fill" },
          { label: "Contain", value: "contain" },
          { label: "Cover", value: "cover" },
          { label: "None", value: "none" },
          { label: "Scale Down", value: "scale-down" }
        ]
      },
      {
        name: "Position",
        slug: "object_position",
        type: "focus-point",
        conditions: ["size.object_fit", "not in", [undefined, "none"]]
      },
      {
        name: "Box Size",
        slug: "box_sizing",
        type: "button-bar",
        width: 12,
        items: [
          { label: "Border Box", value: "border-box" },
          { label: "Content Box", value: "content-box" }
        ]
      }
    ]
  },
  {
    slug: "position",
    name: "Position",
    type: "accordion",
    controls: [
      {
        slug: "position",
        name: "Position",
        type: "select",
        items: [
          { label: "Static", value: "static" },
          { label: "Relative", value: "relative" },
          { label: "Absolute", value: "absolute" },
          { label: "Fixed", value: "fixed" },
          { label: "Sticky", value: "sticky" }
        ]
      },
      {
        slug: "top",
        name: "Top",
        type: "unit",
        width: 6,
        conditions: ["position.position", "not in", ["static", undefined]]
      },
      {
        slug: "right",
        name: "Right",
        type: "unit",
        width: 6,
        conditions: ["position.position", "not in", ["static", undefined]]
      },
      {
        slug: "bottom",
        name: "Bottom",
        type: "unit",
        width: 6,
        conditions: ["position.position", "not in", ["static", undefined]]
      },
      {
        slug: "left",
        name: "Left",
        type: "unit",
        width: 6,
        conditions: ["position.position", "not in", ["static", undefined]]
      },
      {
        slug: "z_index",
        name: "Z-Index",
        type: "number",
        width: 6,
        conditions: ["position.position", "not in", ["static", undefined]]
      }
    ]
  },
  {
    slug: "typography",
    name: "Typography",
    type: "accordion",
    hideAfter: 9,
    controls: [
      {
        slug: "color",
        name: "Color",
        type: "color"
      },
      {
        slug: "font_family",
        name: "Font",
        type: "font-family"
      },
      {
        slug: "font_weight",
        name: "Weight",
        type: "select",
        items: [
          { label: "100 - Thin", value: 100 },
          { label: "200 - Extra Light", value: 200 },
          { label: "300 - Light", value: 300 },
          { label: "400 - Normal", value: 400 },
          { label: "500 - Medium", value: 500 },
          { label: "600 - Semi Bold", value: 600 },
          { label: "700 - Bold", value: 700 },
          { label: "800 - Extra Bold", value: 800 },
          { label: "900 - Black", value: 900 }
        ]
      },
      {
        slug: "font_size",
        name: "Size",
        type: "unit",
        width: 6,
        min: 0
      },
      {
        slug: "line_height",
        name: "Height",
        type: "unit",
        width: 6,
        defaultType: "custom"
      },
      {
        slug: "text_align",
        name: "Align",
        type: "button-bar",
        items: [
          {
            label: "Left",
            value: "start",
            icon: "AlignLeftIcon",
            hideLabel: true
          },
          {
            label: "Center",
            value: "center",
            icon: "AlignCenterIcon",
            hideLabel: true
          },
          {
            label: "Right",
            value: "end",
            icon: "AlignRightIcon",
            hideLabel: true
          },
          {
            label: "Justify",
            value: "justify",
            icon: "AlignJustifyIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "style",
        name: "Style",
        type: "group",
        controls: [
          {
            slug: "text_decoration",
            name: "Decoration",
            type: "button-bar",
            width: 8,
            items: [
              {
                label: "None",
                value: "none",
                icon: "XmarkIcon",
                hideLabel: true
              },
              {
                label: "Underline",
                value: "underline",
                icon: "UnderlineIcon",
                hideLabel: true
              },
              {
                label: "Overline",
                value: "overline",
                icon: "OverlineIcon",
                hideLabel: true
              },
              {
                label: "Line Through",
                value: "line-through",
                icon: "StrikethroughIcon",
                hideLabel: true
              }
            ],
            positionLabel: "bottom"
          },
          {
            slug: "font_style",
            name: "Italic",
            type: "button-bar",
            items: [
              {
                label:
                  "<span style='font-family: monospace;vertical-align: middle;font-weight:normal;'>I</span>",
                value: "normal"
              },
              {
                label: "Italic",
                value: "italic",
                icon: "ItalicIcon",
                hideLabel: true
              }
            ],
            width: 4,
            positionLabel: "bottom"
          }
        ]
      },
      {
        slug: "text_transform",
        name: "Transform",
        type: "button-bar",
        items: [
          {
            label: "None",
            value: "none",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            label: "AA",
            value: "uppercase"
          },
          {
            label: "Aa",
            value: "capitalize"
          },
          {
            label: "aa",
            value: "lowercase"
          }
        ],
        width: 7,
        positionLabel: "bottom"
      },
      {
        slug: "letter_spacing",
        name: "Letter Spacing",
        type: "unit",
        width: 5,
        positionLabel: "bottom"
      },
      {
        slug: "text_indent",
        name: "Text Indent",
        type: "unit",
        width: 6,
        positionLabel: "bottom"
      },
      {
        slug: "direction",
        name: "Direction",
        type: "button-bar",
        items: ["ltr", "rtl"],
        width: 6,
        positionLabel: "bottom"
      },
      {
        slug: "overflow_wrap",
        name: "Overflow Wrap",
        width: 6,
        positionLabel: "bottom",
        type: "select",
        items: ["normal", "break-word"]
      },
      {
        slug: "text_wrap",
        name: "Text Wrap",
        width: 6,
        positionLabel: "bottom",
        type: "select",
        items: [
          {
            label: "auto",
            value: "wrap"
          },
          {
            label: "nowrap",
            value: "nowrap"
          },
          {
            label: "balance",
            value: "balance"
          },
          {
            label: "pretty",
            value: "pretty"
          }
        ]
      },
      {
        slug: "text_overflow",
        name: "Text Overflow",
        type: "button-bar",
        positionLabel: "bottom",
        width: 4,
        items: [
          {
            value: "clip",
            label: "Clip",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: "ellipsis",
            label: "Ellipsis",
            icon: "EllipsisIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "list_style",
        name: "List Style",
        type: "button-bar",
        positionLabel: "bottom",
        width: 8,
        items: [
          {
            value: "none",
            label: "None",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: "disc",
            label: "Circles",
            icon: "ListIcon",
            hideLabel: true
          },
          {
            value: "numbers",
            label: "Numbers",
            icon: "OlListIcon",
            hideLabel: true
          },
          {
            value: "lower-alpha",
            label: "Letters",
            icon: "LetterAIcon",
            hideLabel: true
          },
          {
            value: "lower-roman",
            label: "Roman numerals",
            icon: "LetterIIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "stroke",
        name: "Stroke",
        type: "group",
        controls: [
          {
            slug: "stroke_width",
            name: "Width",
            type: "unit",
            width: 5,
            positionLabel: "bottom"
          },
          {
            slug: "stroke_color",
            name: "Color",
            type: "color",
            width: 7,
            positionLabel: "bottom"
          }
        ]
      },
      {
        slug: "text_shadow",
        name: "Text Shadow",
        type: "repeater",
        itemColorPath: "color",
        titleTemplate: (row: Partial<TextShadowRow>) => {
          const { x, y, blur } = row;
          return `Text Shadow: ${x?.style} ${y?.style} ${blur?.style}`;
        },
        defaultNewValue: {
          x: { number: 0, unit: "px", style: "0px" },
          y: { number: 1, unit: "px", style: "1px" },
          blur: { number: 1, unit: "px", style: "1px" },
          color: "#000000"
        },
        controls: [
          {
            slug: "x",
            name: "X",
            type: "unit",
            min: -20,
            max: 20,
            step: 1
          },
          {
            slug: "y",
            name: "Y",
            type: "unit",
            min: -20,
            max: 20,
            step: 1
          },
          {
            slug: "blur",
            name: "Blur",
            type: "unit",
            min: 0,
            max: 20,
            step: 1
          },
          {
            slug: "color",
            name: "Color",
            type: "color"
          }
        ]
      }
    ]
  },
  {
    slug: "background",
    name: "Background",
    type: "accordion",
    controls: [
      {
        slug: "background_color",
        name: "Color",
        type: "color"
      },
      {
        slug: "backgrounds",
        name: "Image & Gradient",
        type: "repeater",
        defaultNewValue: {
          type: "image"
        },
        itemIcon: "ImageAndVideIcon",
        titleTemplate: row => {
          if (row?.type === "gradient") return "Gradient";
          if (row?.type === "image") return "Image";
          if (row?.type === "color_overlay")
            return row.color || "Color Overlay";
          if (row?.type === "video") return "Video";
          return "";
        },
        defaultTitle: "Background",
        controls: [
          {
            slug: "type",
            name: "Type",
            type: "button-bar",
            hideLabel: true,
            items: [
              {
                label: "Image",
                value: "image",
                icon: "ImageIcon",
                hideLabel: true
              },
              {
                label: "Gradient",
                value: "gradient",
                iconHtml: '<span class="oxy-gradient-icon"></span>',
                hideLabel: true
              },
              {
                label: "Color Overlay",
                value: "color_overlay",
                icon: "SquareSolidIcon",
                hideLabel: true
              }
              // {
              //   label: "Video",
              //   value: "video",
              //   icon: "ClapperboardPlayIcon",
              //   hideLabel: true
              // }
            ]
          },
          {
            slug: "image",
            name: "Image",
            type: "media",
            hideLabel: true,
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "image_size",
            name: "Size",
            type: "media-size",
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "background_size",
            name: "Size",
            type: "button-bar",
            items: [
              {
                label: "Cover",
                value: "cover"
              },
              {
                label: "Contain",
                value: "contain"
              },
              {
                label: "Custom",
                value: "custom"
              }
            ],
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "background_size_custom",
            name: "",
            type: "group",
            controls: [
              {
                slug: "width",
                name: "Width",
                type: "unit",
                width: 6,
                positionLabel: "bottom"
              },
              {
                slug: "height",
                name: "Height",
                type: "unit",
                width: 6,
                positionLabel: "bottom"
              }
            ],
            conditions: [
              ["background.backgrounds[].type", "==", "image"],
              ["background.backgrounds[].background_size", "==", "custom"]
            ]
          },
          {
            slug: "background_position",
            name: "Position",
            type: "focus-point",
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "background_repeat",
            name: "Repeat",
            type: "button-bar",
            items: [
              {
                label: "No Repeat",
                value: "no-repeat",
                icon: "XmarkIcon",
                hideLabel: true
              },
              {
                label: "Repeat",
                value: "repeat",
                icon: "GridIcon",
                hideLabel: true
              },
              {
                label: "Repeat X",
                value: "repeat-x",
                icon: "EllipsisIcon",
                hideLabel: true
              },
              {
                label: "Repeat Y",
                value: "repeat-y",
                icon: "EllipsisVerticalIcon",
                hideLabel: true
              }
            ],
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "background_attachment",
            name: "Attach",
            type: "button-bar",
            items: [
              {
                label: "Scroll",
                value: "scroll"
              },
              {
                label: "Fixed",
                value: "fixed"
              }
            ],
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "background_blend_mode",
            name: "Blend",
            type: "select",
            items: [
              {
                label: "Normal",
                value: "normal"
              },
              {
                label: "Multiply",
                value: "multiply"
              },
              {
                label: "Screen",
                value: "screen"
              },
              {
                label: "Overlay",
                value: "overlay"
              },
              {
                label: "Darken",
                value: "darken"
              },
              {
                label: "Lighten",
                value: "lighten"
              },
              {
                label: "Color Dodge",
                value: "color-dodge"
              },
              {
                label: "Color Burn",
                value: "color-burn"
              },
              {
                label: "Hard Light",
                value: "hard-light"
              },
              {
                label: "Soft Light",
                value: "soft-light"
              },
              {
                label: "Difference",
                value: "difference"
              },
              {
                label: "Exclusion",
                value: "exclusion"
              },
              {
                label: "Hue",
                value: "hue"
              },
              {
                label: "Saturation",
                value: "saturation"
              },
              {
                label: "Color",
                value: "color"
              },
              {
                label: "Luminosity",
                value: "luminosity"
              }
            ],
            conditions: ["background.backgrounds[].type", "==", "image"]
          },
          {
            slug: "color",
            name: "Color",
            type: "color",
            hideLabel: true,
            inline: true,
            positionLabel: "bottom",
            conditions: ["background.backgrounds[].type", "==", "color_overlay"]
          },
          {
            slug: "gradient",
            name: "Gradient",
            type: "color",
            hideLabel: true,
            inline: true,
            positionLabel: "bottom",
            colorType: "gradientOnly",
            conditions: ["background.backgrounds[].type", "==", "gradient"]
          }
          // {
          //   slug: "video",
          //   name: "Video",
          //   type: "video",
          //   hideLabel: true,
          //   conditions: ["type", "==", "video"]
          // }
        ]
      }
    ]
  },
  {
    slug: "borders",
    name: "Borders",
    type: "accordion",
    controls: [
      {
        slug: "border_radius",
        name: "Radius",
        type: "border-radius"
      },
      {
        slug: "borders",
        name: "Borders",
        type: "border",
        positionLabel: "top"
      }
    ]
  },
  {
    slug: "effects",
    name: "Effects",
    type: "accordion",
    controls: [
      {
        slug: "opacity",
        name: "Opacity",
        type: "number",
        min: 0,
        max: 100,
        step: 1
      },
      {
        slug: "cursor",
        name: "Cursor",
        type: "select",
        items: [
          "auto",
          "default",
          "pointer",
          "wait",
          "text",
          "move",
          "not-allowed",
          "help",
          "crosshair",
          "grab",
          "grabbing"
        ]
      },
      {
        slug: "blend_mode",
        name: "Blend",
        type: "select",
        items: [
          {
            label: "Normal",
            value: "normal"
          },
          {
            label: "Multiply",
            value: "multiply"
          },
          {
            label: "Screen",
            value: "screen"
          },
          {
            label: "Overlay",
            value: "overlay"
          },
          {
            label: "Darken",
            value: "darken"
          },
          {
            label: "Lighten",
            value: "lighten"
          },
          {
            label: "Color Dodge",
            value: "color-dodge"
          },
          {
            label: "Color Burn",
            value: "color-burn"
          },
          {
            label: "Hard Light",
            value: "hard-light"
          },
          {
            label: "Soft Light",
            value: "soft-light"
          },
          {
            label: "Difference",
            value: "difference"
          },
          {
            label: "Exclusion",
            value: "exclusion"
          },
          {
            label: "Hue",
            value: "hue"
          },
          {
            label: "Saturation",
            value: "saturation"
          },
          {
            label: "Color",
            value: "color"
          },
          {
            label: "Luminosity",
            value: "luminosity"
          }
        ]
      },
      {
        slug: "transition",
        name: "Transition",
        type: "repeater",
        titleTemplate: (row: TransitionRow) => {
          const property = (row?.property as string) || "all";
          const capitalizedProp =
            property.charAt(0).toUpperCase() + property.slice(1);

          return `${capitalizedProp}: ${row?.duration?.style || "0ms"}`;
        },
        itemIcon: "TimerIcon",
        defaultNewValue: {
          property: "opacity",
          duration: { number: "200", unit: "ms", style: "200ms" },
          easing: "ease"
        },
        controls: [
          {
            slug: "property",
            name: "Property",
            type: "select",
            items: [
              "all",
              "none",
              "color",
              "background",
              "transform",
              "width",
              "height",
              "opacity",
              "border",
              "box-shadow",
              "outline"
            ]
          },
          {
            slug: "duration",
            name: "Duration",
            type: "unit",
            min: 0,
            step: 25,
            max: 1000,
            types: ["ms", "s", "custom"],
            defaultType: "ms"
          },
          {
            slug: "easing",
            name: "Easing",
            type: "select",
            items: ["ease", "linear", "ease-in", "ease-out", "ease-in-out"]
          },
          {
            slug: "delay",
            name: "Delay",
            type: "unit",
            min: 0,
            step: 25,
            max: 1000,
            types: ["ms", "s", "custom"],
            defaultType: "ms"
          }
        ]
      },
      {
        slug: "box_shadow",
        name: "Box Shadow",
        type: "repeater",
        itemColorPath: "color",
        titleTemplate: (row: Partial<BoxShadowRow>) => {
          const { x, y, blur, spread } = row;
          const position = row.position === "inset" ? "Inner" : "Outer";
          return `${position}: ${x?.style} ${y?.style} ${blur?.style} ${spread?.style}`;
        },
        defaultNewValue: {
          x: { number: 0, unit: "px", style: "0px" },
          y: { number: 2, unit: "px", style: "2px" },
          blur: { number: 5, unit: "px", style: "5px" },
          spread: { number: 0, unit: "px", style: "0px" },
          color: "#00000030",
          position: "outset"
        },
        controls: [
          {
            slug: "position",
            name: "Position",
            type: "button-bar",
            items: [
              {
                label: "Outset",
                value: "outset"
              },
              {
                label: "Inset",
                value: "inset"
              }
            ]
          },
          {
            slug: "color",
            name: "Color",
            type: "color"
          },
          {
            slug: "x",
            name: "X",
            type: "unit"
          },
          {
            slug: "y",
            name: "Y",
            type: "unit"
          },
          {
            slug: "blur",
            name: "Blur",
            type: "unit"
          },
          {
            slug: "spread",
            name: "Spread",
            type: "unit"
          }
        ]
      },
      {
        slug: "filter",
        name: "Filter",
        type: "repeater",
        itemIcon: "WandMagicSparkles",
        defaultNewValue: {
          type: "blur",
          value: { number: 100, unit: "%", style: "100%" },
          blur_value: { number: 5, unit: "px", style: "5px" },
          hue_value: { number: 180, unit: "deg", style: "180deg" },
          x: { number: 0, unit: "px", style: "0px" },
          y: { number: 2, unit: "px", style: "2px" },
          blur: { number: 5, unit: "px", style: "5px" }
        },
        titleTemplate: (row: Partial<FilterRow>) => {
          if (row.type === "drop-shadow") {
            return `Drop shadow: ${row.x?.style} ${row.y?.style} ${row.blur?.style}`;
          }

          // Formatted type
          const type = row.type
            ? row.type?.charAt(0).toUpperCase() +
              row.type?.slice(1).replace("-", " ")
            : "Filter";

          if (row.type === "blur") {
            return `${type}: ${row.blur_value?.style}`;
          }

          if (row.type === "hue-rotate") {
            return `${type}: ${row.hue_value?.style}`;
          }

          return `${type}: ${row.value?.style}`;
        },
        controls: [
          {
            slug: "type",
            name: "Type",
            type: "select",
            items: [
              {
                label: "Blur",
                value: "blur"
              },
              {
                label: "Brightness",
                value: "brightness"
              },
              {
                label: "Contrast",
                value: "contrast"
              },
              {
                label: "Grayscale",
                value: "grayscale"
              },
              {
                label: "Hue Rotate",
                value: "hue-rotate"
              },
              {
                label: "Invert",
                value: "invert"
              },
              {
                label: "Opacity",
                value: "opacity"
              },
              {
                label: "Saturate",
                value: "saturate"
              },
              {
                label: "Sepia",
                value: "sepia"
              }
            ]
          },
          {
            slug: "blur_value",
            name: "Value",
            type: "unit",
            conditions: ["effects.filter[].type", "==", "blur"]
          },
          {
            slug: "hue_value",
            name: "Value",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            min: 0,
            max: 360,
            step: 45,
            conditions: ["effects.filter[].type", "==", "hue-rotate"]
          },
          {
            slug: "value",
            name: "Value",
            type: "unit",
            min: 0,
            max: 100,
            step: 1,
            defaultType: "%",
            types: ["%"],
            conditions: [
              "effects.filter[].type",
              "in",
              [
                "brightness",
                "contrast",
                "grayscale",
                "invert",
                "opacity",
                "saturate",
                "sepia"
              ]
            ]
          },
          {
            slug: "x",
            name: "X",
            type: "unit",
            conditions: ["effects.filter[].type", "==", "drop-shadow"]
          },
          {
            slug: "blur",
            name: "Blur",
            type: "unit",
            conditions: ["effects.filter[].type", "==", "drop-shadow"]
          }
        ]
      },
      {
        slug: "backdrop_filter",
        name: "Backdrop Filter",
        type: "repeater",
        itemIcon: "SquareQuestionIcon",
        defaultNewValue: {
          type: "blur",
          value: { number: 100, unit: "%", style: "100%" },
          blur_value: { number: 5, unit: "px", style: "5px" },
          hue_value: { number: 180, unit: "deg", style: "180deg" },
          x: { number: 0, unit: "px", style: "0px" },
          y: { number: 2, unit: "px", style: "2px" },
          blur: { number: 5, unit: "px", style: "5px" }
        },
        titleTemplate: (row: Partial<FilterRow>) => {
          if (row.type === "drop-shadow") {
            return `Drop shadow: ${row.x?.style} ${row.y?.style} ${row.blur?.style}`;
          }

          // Formatted type
          const type = row.type
            ? row.type?.charAt(0).toUpperCase() +
              row.type?.slice(1).replace("-", " ")
            : "Filter";

          if (row.type === "blur") {
            return `${type}: ${row.blur_value?.style}`;
          }

          if (row.type === "hue-rotate") {
            return `${type}: ${row.hue_value?.style}`;
          }

          return `${type}: ${row.value?.style}`;
        },
        controls: [
          {
            slug: "type",
            name: "Type",
            type: "select",
            items: [
              {
                label: "Blur",
                value: "blur"
              },
              {
                label: "Brightness",
                value: "brightness"
              },
              {
                label: "Contrast",
                value: "contrast"
              },
              {
                label: "Grayscale",
                value: "grayscale"
              },
              {
                label: "Hue Rotate",
                value: "hue-rotate"
              },
              {
                label: "Invert",
                value: "invert"
              },
              {
                label: "Opacity",
                value: "opacity"
              },
              {
                label: "Saturate",
                value: "saturate"
              },
              {
                label: "Sepia",
                value: "sepia"
              }
            ]
          },
          {
            slug: "blur_value",
            name: "Value",
            type: "unit",
            conditions: ["effects.backdrop_filter[].type", "==", "blur"]
          },
          {
            slug: "hue_value",
            name: "Value",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            min: 0,
            max: 360,
            step: 45,
            conditions: ["effects.backdrop_filter[].type", "==", "hue-rotate"]
          },
          {
            slug: "value",
            name: "Value",
            type: "unit",
            min: 0,
            max: 100,
            step: 1,
            defaultType: "%",
            types: ["%"],
            conditions: [
              "effects.backdrop_filter[].type",
              "in",
              [
                "brightness",
                "contrast",
                "grayscale",
                "invert",
                "opacity",
                "saturate",
                "sepia"
              ]
            ]
          },
          {
            slug: "x",
            name: "X",
            type: "unit",
            conditions: ["effects.backdrop_filter[].type", "==", "drop-shadow"]
          },
          {
            slug: "blur",
            name: "Blur",
            type: "unit",
            conditions: ["effects.backdrop_filter[].type", "==", "drop-shadow"]
          }
        ]
      },
      {
        slug: "transform",
        name: "Transform",
        type: "repeater",
        itemIcon: "LeftAndRightArrowsIcon",
        defaultNewValue: {
          type: "translate",

          translate_x: { number: 0, unit: "px", style: "0px" },
          translate_y: { number: 0, unit: "px", style: "0px" },
          translate_z: { number: 0, unit: "px", style: "0px" },

          scale_x: 1,
          scale_y: 1,
          scale_z: 1,

          rotate_x: { number: 0, unit: "deg", style: "0deg" },
          rotate_y: { number: 0, unit: "deg", style: "0deg" },
          rotate_z: { number: 0, unit: "deg", style: "0deg" },

          skew_x: { number: 0, unit: "deg", style: "0deg" },
          skew_y: { number: 0, unit: "deg", style: "0deg" }
        },
        titleTemplate: (row: Partial<TransformRow>) => {
          if (row.type === "translate") {
            return `Translate: ${row.translate_x?.style}, ${row.translate_y?.style}, ${row.translate_z?.style}`;
          }

          if (row.type === "scale") {
            return `Scale: ${row.scale_x}, ${row.scale_y}, ${row.scale_z}`;
          }

          if (row.type === "rotate") {
            return `Rotate: ${row.rotate_x?.style}, ${row.rotate_y?.style}, ${row.rotate_z?.style}`;
          }

          if (row.type === "skew") {
            return `Skew: ${row.skew_x?.style}, ${row.skew_y?.style}`;
          }

          return "Transform";
        },
        controls: [
          {
            slug: "type",
            name: "Type",
            type: "button-bar",
            hideLabel: true,
            items: [
              {
                label: "Translate",
                value: "translate"
              },
              {
                label: "Scale",
                value: "scale"
              },
              {
                label: "Rotate",
                value: "rotate"
              },
              {
                label: "Skew",
                value: "skew"
              }
            ]
          },
          {
            slug: "translate_x",
            name: "X",
            type: "unit",
            conditions: ["effects.transform[].type", "==", "translate"]
          },
          {
            slug: "translate_y",
            name: "Y",
            type: "unit",
            conditions: ["effects.transform[].type", "==", "translate"]
          },
          {
            slug: "translate_z",
            name: "Z",
            type: "unit",
            conditions: ["effects.transform[].type", "==", "translate"]
          },
          {
            slug: "scale_x",
            name: "X",
            type: "number",
            min: 0,
            step: 0.1,
            max: 2,
            conditions: ["effects.transform[].type", "==", "scale"]
          },
          {
            slug: "scale_y",
            name: "Y",
            type: "number",
            min: 0,
            step: 0.1,
            max: 2,
            conditions: ["effects.transform[].type", "==", "scale"]
          },
          {
            slug: "scale_z",
            name: "Z",
            type: "number",
            min: 0,
            step: 0.1,
            max: 2,
            conditions: ["effects.transform[].type", "==", "scale"]
          },
          {
            slug: "rotate_x",
            name: "X",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            conditions: ["effects.transform[].type", "==", "rotate"]
          },
          {
            slug: "rotate_y",
            name: "Y",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            conditions: ["effects.transform[].type", "==", "rotate"]
          },
          {
            slug: "rotate_z",
            name: "Z",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            conditions: ["effects.transform[].type", "==", "rotate"]
          },
          {
            slug: "skew_x",
            name: "X",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            conditions: ["effects.transform[].type", "==", "skew"]
          },
          {
            slug: "skew_y",
            name: "Y",
            type: "unit",
            defaultType: "deg",
            types: ["deg"],
            conditions: ["effects.transform[].type", "==", "skew"]
          }
        ]
      },
      {
        slug: "pointer_events",
        name: "Events",
        type: "button-bar",
        items: ["auto", "none"]
      }
    ]
  },
  {
    slug: "flex_child",
    name: "Flex Child",
    type: "accordion",
    conditions: findLastParentClassThatHasDisplay(["flex", "inline-flex"]),
    controls: [
      {
        slug: "flex_grow",
        name: "Grow",
        type: "number",
        width: 4,
        positionLabel: "bottom"
      },
      {
        slug: "flex_shrink",
        name: "Shrink",
        type: "number",
        width: 4,
        positionLabel: "bottom"
      },
      {
        slug: "flex_basis",
        name: "Basis",
        type: "unit",
        width: 4,
        positionLabel: "bottom"
      },
      {
        slug: "align_self",
        name: "Align",
        type: "button-bar",
        items: [
          {
            value: "auto",
            label: "Auto",
            icon: "SquareQuestionIcon",
            hideLabel: true
          },
          {
            value: "start",
            label: "Start",
            icon: "FlexAlignLeftIcon",
            hideLabel: true
          },
          {
            value: "center",
            label: "Center",
            icon: "FlexAlignCenterHorizontalIcon",
            hideLabel: true
          },
          {
            value: "end",
            label: "End",
            icon: "FlexAlignRightIcon",
            hideLabel: true
          },
          {
            value: "stretch",
            label: "Stretch",
            icon: "SquareQuestionIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "order",
        name: "Order",
        type: "button-bar",
        items: [
          {
            label: "None",
            value: "none",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: -1,
            label: "First"
          },
          {
            value: 99,
            label: "Last"
          },
          {
            value: "custom",
            label: "Custom"
          }
        ]
      },
      {
        slug: "order_custom",
        name: "Order",
        type: "number",
        width: 6,
        conditions: ["flex_child.order", "==", "custom"]
      }
    ]
  },
  {
    slug: "grid_child",
    name: "Grid Child",
    type: "accordion",
    conditions: findLastParentClassThatHasDisplay(["grid", "inline-grid"]),
    controls: [
      {
        slug: "row_start",
        name: "Row Start",
        type: "text",
        width: 3,
        positionLabel: "bottom",
        disabledConditions: ["grid_child.area", "not in", [undefined, null]]
      },
      {
        slug: "column_start",
        name: "Col. Start",
        type: "text",
        width: 3,
        positionLabel: "bottom",
        disabledConditions: ["grid_child.area", "not in", [undefined, null]]
      },
      {
        slug: "row_end",
        name: "Row End",
        type: "text",
        width: 3,
        positionLabel: "bottom",
        disabledConditions: ["grid_child.area", "not in", [undefined, null]]
      },
      {
        slug: "column_end",
        name: "Col. End",
        type: "text",
        width: 3,
        positionLabel: "bottom",
        disabledConditions: ["grid_child.area", "not in", [undefined, null]]
      },
      {
        slug: "area",
        name: "Area",
        type: "text"
      },
      {
        slug: "align_self",
        name: "Align",
        type: "button-bar",
        items: [
          {
            value: "auto",
            label: "Auto",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: "start",
            label: "Start",
            icon: "FlexAlignTopIcon",
            hideLabel: true
          },
          {
            value: "center",
            label: "Center",
            icon: "FlexAlignCenterVerticalIcon",
            hideLabel: true
          },
          {
            value: "end",
            label: "End",
            icon: "FlexAlignBottomIcon",
            hideLabel: true
          },
          {
            value: "stretch",
            label: "Stretch",
            icon: "SquareQuestionIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "justify_self",
        name: "Justify",
        type: "button-bar",
        items: [
          {
            value: "auto",
            label: "Auto",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: "start",
            label: "Start",
            icon: "FlexAlignLeftIcon",
            hideLabel: true
          },
          {
            value: "center",
            label: "Center",
            icon: "FlexAlignCenterHorizontalIcon",
            hideLabel: true
          },
          {
            value: "end",
            label: "End",
            icon: "FlexAlignRightIcon",
            hideLabel: true
          },
          {
            value: "stretch",
            label: "Stretch",
            icon: "SquareQuestionIcon",
            hideLabel: true
          }
        ]
      },
      {
        slug: "order",
        name: "Order",
        type: "button-bar",
        items: [
          {
            label: "None",
            value: "none",
            icon: "XmarkIcon",
            hideLabel: true
          },
          {
            value: -1,
            label: "First"
          },
          {
            value: 99,
            label: "Last"
          },
          {
            value: "custom",
            label: "Custom"
          }
        ]
      },
      {
        slug: "order_custom",
        name: "Order",
        type: "number",
        width: 6,
        conditions: ["grid_child.order", "==", "custom"]
      }
    ]
  },
  {
    slug: "custom_css",
    name: "Custom CSS",
    type: "accordion",
    controls: [
      {
        slug: "custom_css",
        name: "Custom CSS",
        type: "code",
        language: "css",
        hideLabel: true,
        placeholder: ":selector {\n \n}",
        autofillOnEmpty: ":selector {\n  PLACECURSORHERE\n}"
      }
    ]
  }
  // {
  //   slug: "other",
  //   name: "Other",
  //   type: "accordion",
  //   controls: [
  //     {
  //       slug: "variables",
  //       name: "CSS Variables",
  //       type: "variables"
  //     },
  //     {
  //       slug: "custom_css",
  //       name: "Custom CSS",
  //       type: "code",
  //       hideLabel: true
  //     }
  //   ]
  // }
];
